import {
  APP_LANGUAGE_SUPPORTS_KEYS,
  AppLanguage,
  getLocalizationCode,
} from '@/shared/lib/appLanguageSupports';

import {buildAppLanguagePath} from './buildAppLanguagePath';
import {ensureTrailingPathSlash} from './ensureTrailingSlash';

/**
 * Flat route ids are their file names, so every localized route carries
 * `($lang)` in its id: `routes/($lang)._index`, `routes/($lang).community`.
 *
 * The matched params cannot stand in for this check. `($lang)` is optional,
 * and React Router explodes optional segments into separate branches, so
 * `/community/` matches `($lang).community` with no `lang` param at all —
 * indistinguishable from `/login`, which is exactly the default-language page
 * these tags matter most for.
 */
const LOCALIZED_ROUTE_ID_SEGMENT = '($lang)';

/** `($lang).$` is the catch-all 404, which should not advertise alternates. */
const SPLAT_ROUTE_ID_SUFFIX = '.$';

export type SeoLink = {
  rel: 'canonical' | 'alternate';
  href: string;
  hrefLang?: string;
};

export function getLocalizedSeoLinks(
  routeId: string | undefined,
  pathname: string,
  lang: AppLanguage
): SeoLink[] {
  if (
    !routeId?.includes(LOCALIZED_ROUTE_ID_SEGMENT) ||
    routeId.endsWith(SPLAT_ROUTE_ID_SUFFIX)
  ) {
    return [];
  }

  return [
    {
      rel: 'canonical',
      href: ensureTrailingPathSlash(pathname),
    },
    ...APP_LANGUAGE_SUPPORTS_KEYS.filter((pickLang) => pickLang !== lang).map(
      (language): SeoLink => ({
        rel: 'alternate',
        href: ensureTrailingPathSlash(buildAppLanguagePath(pathname, language)),
        hrefLang: getLocalizationCode(language),
      })
    ),
    {
      rel: 'alternate',
      href: ensureTrailingPathSlash(buildAppLanguagePath(pathname, 'en')),
      hrefLang: 'x-default',
    },
  ];
}
